<template>
<div class="chapter">
<div class="inner_page">
<p class="ch-title" id="sec1"><a id="page_12"></a>Tablet Purse</p>
<p class="noindentr"><b>Finished size: 9½˝ × 11˝</b></p>
<p class="image1"><img alt="" src="images/pg12_1.jpg"/></p>
<p class="cap2">A tablet purse, embellished and embroidered, makes for a very glamorous accessory.</p>
<div class="box">
<p class="head1"><b>FABRIC AND SUPPLIES</b></p>
<p class="bullets">•<span class="padding"></span>Assorted fabric scraps at least 5˝ × 8˝</p>
<p class="bullets">•<span class="padding"></span>2 pieces of foundation fabric 13˝ × 14˝</p>
<p class="bullets">•<span class="padding"></span>2 pieces of lining fabric 11˝ × 12˝</p>
<p class="bullets">•<span class="padding"></span>2 pieces of lightweight fusible fleece 11˝ × 12˝ (I use Vilene H630 by Pellon.)</p>
<p class="bullets">•<span class="padding"></span>1 piece of backing fabric 11˝ × 12˝</p>
<p class="bullets">•<span class="padding"></span>1 rectangular purse frame 3½˝ × 9½˝</p>
<p class="head"><b>NEEDLES AND THREADS</b></p>
<p class="bullets">•<span class="padding"></span>Milliners #03 needle for</p>
<p class="bullets-in">Perle cotton #8 and #12 thread</p>
<p class="bullets-in">Embroidery floss</p>
<p class="bullets">•<span class="padding"></span>Milliners #09 needle for</p>
<p class="bullets-in">Nymo beading thread</p>
<p class="bullets">•<span class="padding"></span>Chenille #22 needle for</p>
<p class="bullets-in">2mm, 4mm, 7mm, and 13mm silk ribbons</p>
<p class="bullets-in">Silk perle #8 and #12 thread</p>
<p class="bullets-in">Rayon ribbon floss</p>
<p class="bullets-in">Velvet thread</p>
<p class="head"><b>EMBELLISHMENTS</b></p>
<p class="bullets">•<span class="padding"></span>7mm silk ribbon for outer trim of completed tablet purse</p>
<p class="bullets">•<span class="padding"></span>Small sequins</p>
<p class="bullets">•<span class="padding"></span>Seed beads</p>
<p class="bullets">•<span class="padding"></span>Selection of assorted beads for back</p>
</div>
<p class="image1"><img alt="" src="images/pg13_1.jpg"/></p>
<p class="cap">Fabrics, needles, and frame accessories</p>
<p class="h1">Construction</p>
<p class="noindent"><i>Pattern includes ¼˝ seam allowance.</i></p>
<p class="h2">Making the Front Panel</p>
<p class="noindent">Use Pattern 1 for the front panel.</p>
<p class="number"><span class="red1"><b>1.</b></span> Follow the foundation piecing method to complete the front panel.</p>
<p class="number"><span class="red1"><b>2.</b></span> Follow the stitch maps to complete the seam treatments.</p>
<p class="number"><span class="red1"><b>3.</b></span> Trim the completed front panel on the cutting line.</p>
<p class="h1-l"><a id="page_14"></a>Stitch Maps</p>
<p class="h3">SEAM TREATMENT A</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg14_1.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Plume stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Stab stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Stem stitch</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
<p class="alpha"><span class="sans">D.</span><span class="padding"></span>Lazy daisy stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
</div>
<p class="h3">SEAM TREATMENT B</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg14_2.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Stab stitch</p>
<p class="bullet1">•<span class="padding"></span>Silk perle #8 thread</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Knotted fly stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Lazy daisy stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">D.</span><span class="padding"></span>Stem stitch</p>
<p class="bullet1">•<span class="padding"></span>Silk perle #8 thread</p>
<p class="alpha"><span class="sans">E.</span><span class="padding"></span>Herringbone stitch</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
</div>
<p class="h3">SEAM TREATMENT C</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg14_3.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Stab stitch leaf</p>
<p class="bullet1">•<span class="padding"></span>Rayon ribbon floss</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Loop stitch and flower</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Stem stitch</p>
<p class="bullet1">•<span class="padding"></span>2mm silk ribbon</p>
<p class="alpha"><span class="sans">D.</span><span class="padding"></span>Stab stitch</p>
<p class="bullet1">•<span class="padding"></span>2mm silk ribbon</p>
</div>
<p class="h3">SEAM TREATMENT D</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg15_1.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Colonial knot</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Stab stitch</p>
<p class="bullet1-in">Perle cotton #8 thread</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Lazy daisy stitch</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
<p class="alpha"><span class="sans">D.</span><span class="padding"></span>Cretan stitch</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
<p class="alpha"><span class="sans">E.</span><span class="padding"></span>Lazy daisy stitch</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
<p class="alpha"><span class="sans">F.</span><span class="padding"></span>Single-bead stitch</p>
<p class="bullet1">•<span class="padding"></span>Nymo beading thread</p>
<p class="alpha"><span class="sans">G.</span><span class="padding"></span>Lazy daisy stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
</div>
<p class="h3">SEAM TREATMENT E</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg15_2.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Padded stab stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Twisted chain stitch</p>
<p class="bullet1">•<span class="padding"></span>Silk perle #8 thread</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Lazy daisy stitch with bead</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #12 thread</p>
<p class="alpha"><span class="sans">D.</span><span class="padding"></span>Stab stitch</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #12 thread</p>
</div>
<p class="h3">SEAM TREATMENT F</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg15_3.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Fly stitch leaf</p>
<p class="bullet1">•<span class="padding"></span>Silk perle #8 thread</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Beaded/couched sequin</p>
<p class="bullet1">•<span class="padding"></span>Nymo beading thread</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Zigzag chain stitch</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
<p class="alpha"><span class="sans">D.</span><span class="padding"></span>Colonial knot</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
</div>
<p class="h3">SEAM TREATMENT G</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg15_4.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Stab stitch</p>
<p class="bullet1">•<span class="padding"></span>Silk perle #8 thread</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Lazy daisy stitch</p>
<p class="bullet1">•<span class="padding"></span>Silk perle #8 thread</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Colonial knot</p>
<p class="bullet1">•<span class="padding"></span>Silk perle #8 thread</p>
<p class="alpha"><span class="sans">D.</span><span class="padding"></span>Stem stitch with beads</p>
<p class="bullet1">•<span class="padding"></span>Silk perle #12 thread</p>
</div>
<p class="h3">SEAM TREATMENT H</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg16_1.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Free-form flower with beaded center</p>
<p class="bullet1">•<span class="padding"></span>13mm silk ribbon</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Colonial knot</p>
<p class="bullet1">•<span class="padding"></span>7mm silk ribbon</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Ruched rose</p>
<p class="bullet1">•<span class="padding"></span>7mm silk ribbon</p>
<p class="alpha"><span class="sans">D.</span><span class="padding"></span>Stab stitch</p>
<p class="bullet1">•<span class="padding"></span>2mm silk ribbon</p>
<p class="alpha"><span class="sans">E.</span><span class="padding"></span>Ribbon stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">F.</span><span class="padding"></span>Fishbone stitch</p>
<p class="bullet1">•<span class="padding"></span>Velvet thread</p>
</div>
<p class="h3">SEAM TREATMENT I</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg16_2.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Lazy daisy stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Stem stitch</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Fishbone stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
</div>
<p class="h3">SEAM TREATMENT J</p>
<div class="box1">
<p class="image1"><img alt="" src="images/pg16_2.jpg"/></p>
</div>
<div class="box2">
<p class="alpha"><span class="sans">A.</span><span class="padding"></span>Lazy daisy stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">B.</span><span class="padding"></span>Ribbon stitch</p>
<p class="bullet1">•<span class="padding"></span>4mm silk ribbon</p>
<p class="alpha"><span class="sans">C.</span><span class="padding"></span>Feather stitch</p>
<p class="bullet1">•<span class="padding"></span>Perle cotton #8 thread</p>
<p class="alpha"><span class="sans">D.</span><span class="padding"></span>Beaded/couched sequin</p>
<p class="bullet1">•<span class="padding"></span>Nymo beading thread</p>
<p class="alpha"><span class="sans">E.</span><span class="padding"></span>Stab stitch</p>
<p class="bullet1">•<span class="padding"></span>A single strand of embroidery floss</p>
<p class="alpha"><span class="sans">F.</span><span class="padding"></span>Stab stitch</p>
<p class="bullet1">•<span class="padding"></span>A single strand of embroidery floss</p>
</div>
<p class="h2"><a id="page_17"></a>Making the Back Panel</p>
<p class="noindent">Use Pattern 1 (pullout page P2) for the back panel.</p>
<p class="number"><span class="red1"><b>1.</b></span> Trace the perimeter lines of Pattern 1 onto the right side of the backing fabric.</p>
<p class="number"><span class="red1"><b>2.</b></span> Fuse the fleece to the wrong side of the backing fabric.</p>
<p class="number"><span class="red1"><b>3.</b></span> Use a Hera marker to mark diagonal lines 2˝ apart on the right side of the fabric.</p>
<p class="number"><span class="red1"><b>4.</b></span> Using perle cotton #12 thread or 3 strands of embroidery floss, hand quilt a running stitch along the marked lines. After every 2 stitches, pick up a bead to add texture.</p>
<p class="number"><span class="red1"><b>5.</b></span> Trim the completed back panel on the cutting line.</p>
<p class="image1"><img alt="" src="images/pg17_1.jpg"/></p>
<p class="cap1">Bead to add texture</p>
<p class="h2">Adding the Lining</p>
<p class="number"><span class="red1"><b>1.</b></span> Trace the perimeter lines of the front panel onto the wrong sides of 2 pieces of lining fabric. Mark the 2 *’s on the fabric.</p>
<p class="number"><span class="red1"><b>2.</b></span> Put the front panel on a lining piece with rights sides together. Sew the top edge from * to *.</p>
<p class="image1"><img alt="" src="images/pg17_2.jpg"/></p>
<p class="cap">Sew top edge.</p>
<p class="number"><span class="red1"><b>3.</b></span> Repeat Steps 1 and 2 for the quilted back panel and the other piece of lining fabric.</p>
<p class="h2">Assembly</p>
<p class="number"><span class="red1"><b>1.</b></span> Pin only the lining panels, right sides together, and sew around the extended side seams of the <i>lining fabric only</i> from * to *, leaving a 4˝ opening at the bottom of the lining fabrics for turning.</p>
<p class="image1"><img alt="" src="images/pg17_3.jpg"/></p>
<p class="cap">Sew around extended side seams.</p>
<p class="number"><span class="red1"><b>2.</b></span> Pin the front and side panels together and sew around the side seams from * to *.</p>
<p class="image1"><img alt="" src="images/pg17_4.jpg"/></p>
<p class="cap">Sew around side seams.</p>
<p class="number"><span class="red1"><b>3.</b></span> Gently turn the purse right side out through the opening. Use a spatula to gently push out the stitched seams; press lightly. Slipstitch the opening closed.</p>
<p class="number"><span class="red1"><b>4.</b></span> Open the purse frame and push the fabric into the frame.</p>
<p class="number"><span class="red1"><b>5.</b></span> Using 2 strands of perle cotton #8 thread and a milliners needle, stitch the purse to the frame, starting at the center and working outward, using a running stitch to go in and out of the holes in the frame.</p>
<p class="number"><span class="red1"><b>6.</b></span> Complete the border trim using a 7mm silk ribbon to make a ruched silk ribbon garland.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 07_chapter1.vue?2f7bd7cc